/**
 * Task 280C — derived weekly hint for the package editor's Social service config.
 *
 * The agency enters MONTHLY counts (designs / videos / stories) in the package
 * editor; this derives the approximate WEEKLY publishing workload so the editor
 * can display it as guidance. The package editor is the source of truth — this
 * is the only place the monthly counts live (in `config.social.monthly`); the
 * assignment snapshot never carries them, so the derivation cannot live on the
 * client/Operating-Setup side.
 *
 * Rule (documented): the social `monthly` quota is inherently monthly, so
 * weekly = round(monthly / 4) (round-half-up), clamped to >= 0. Within the
 * social config the feed/static bucket is just `designs` (there is no separate
 * `post` field here, so the no-double-count max rule degenerates to designs).
 * Reels/videos come from `videos`. Stories are VOLUME ONLY — surfaced as a
 * monthly figure and excluded from the weekly slot figures (stories can be many
 * per day and are not scheduled like feed posts/reels in V1).
 */

import type { SocialContentQuota } from '@kmi/contracts';

export interface SocialWeeklyHint {
  /** Feed/static-post slots per week ≈ round(monthly designs / 4). */
  readonly feedSlotsPerWeek: number;
  /** Reels/videos slots per week ≈ round(monthly videos / 4). */
  readonly reelsVideosSlotsPerWeek: number;
  /** Stories per month — volume only, never a weekly slot in V1. */
  readonly storiesMonthlyVolume: number;
}

/** round-half-up monthly→weekly (÷4), guarding null/negative/non-finite to 0. */
function monthlyToWeekly(monthly: number): number {
  if (!Number.isFinite(monthly) || monthly <= 0) return 0;
  return Math.round(monthly / 4);
}

function asVolume(value: number): number {
  return Number.isFinite(value) && value > 0 ? value : 0;
}

export function socialWeeklyHint(monthly: SocialContentQuota): SocialWeeklyHint {
  return {
    feedSlotsPerWeek: monthlyToWeekly(monthly.designs),
    reelsVideosSlotsPerWeek: monthlyToWeekly(monthly.videos),
    storiesMonthlyVolume: asVolume(monthly.stories),
  };
}

/**
 * Task 280D — the auto-mode weekly quota derived from the monthly counts, used
 * to pre-fill the manual override fields when the agency switches off
 * auto-calculate. Designs/videos round ÷4; stories = 0 (volume only — auto must
 * never imply a weekly story slot).
 */
export function autoWeeklyQuota(monthly: SocialContentQuota): SocialContentQuota {
  return {
    designs: monthlyToWeekly(monthly.designs),
    videos: monthlyToWeekly(monthly.videos),
    stories: 0,
  };
}

/** True when a weekly quota is all-zero — the signal for auto (derive-from-monthly) mode. */
export function quotaIsZero(quota: SocialContentQuota): boolean {
  return quota.designs === 0 && quota.videos === 0 && quota.stories === 0;
}

/** Clamp a raw monthly count to a non-negative integer (guards null/negative/non-finite → 0). */
export function clampMonthlyCount(value: number): number {
  return Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
}

/**
 * Task 280B — monthly calendar-slot count for a social service = feed/static
 * (designs) + reels/videos (videos). Stories are EXCLUDED (volume only), and
 * there is no separate `post` field in the social config so feed/static uses
 * `designs` with nothing to double-count. This mirrors the bucketing of
 * `socialWeeklyHint` at monthly granularity and feeds the AI `entry_count`
 * default — which is a per-month item count.
 */
export function monthlyContentEntryCount(monthly: SocialContentQuota): number {
  return clampMonthlyCount(monthly.designs) + clampMonthlyCount(monthly.videos);
}
